import React, { useState, useEffect } from 'react';
import { useAxios } from '../utils/httpClient';
import { useStore } from '../state/store';
import { useRealtimeClient } from '../state/realtime.context';
import { Done } from '../components/done';
import { AxiosInstance } from 'axios';
import { IDone } from '../types/done';

function fetchTasks(
    httpClient: AxiosInstance,
    setDone: (done: IDone) => void,
    setAreAllTasksVisible: (visible: boolean) => void,
    take = 20,
    ownerId?: string,
    assigneeId?: string,
    toDoDate?: string,
    createdAt?: string
) {
    return httpClient
        .get('tasks/completed', {
            params: {
                All: true,
                Take: take,
                CreatorId: ownerId,
                AssigneeId: assigneeId,
                ToDoDate: toDoDate,
                CreatedAt: createdAt
            }
        })
        .then(res => {
            const tasks = res.data.items || [];
            setDone({ items: tasks });
            if (tasks.length < take) {
                setAreAllTasksVisible(true);
            }
            return tasks;
        })
        .catch(err => console.error('Error fetching tasks:', err));
}

export const DoneContainer = () => {
    const httpClient = useAxios();
    const setDone = useStore(state => state.setDone);
    const done = useStore(state => state.done);
    const [areAllTasksVisible, setAreAllTasksVisible] = useState(false);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [lastToDoDate, setLastToDoDate] = useState<string | undefined>(undefined);
    const [lastCreatedAt, setLastCreatedAt] = useState<string | undefined>(undefined);
    const realtime = useRealtimeClient();
    const { ownerId, assigneeId } = useStore(state => ({ ownerId: state.ownerId, assigneeId: state.assigneeId }));

    useEffect(() => {
        // Reset pagination state when filters change
        setAreAllTasksVisible(false);
        setLastToDoDate(undefined);
        setLastCreatedAt(undefined);

        // Initial load - no date filtering, just fetch first 20 tasks
        fetchTasks(
            httpClient,
            setDone,
            setAreAllTasksVisible,
            20,
            ownerId,
            assigneeId,
            undefined, // No date filter on initial load
            undefined  // No date filter on initial load
        ).then((tasks) => {
            if (tasks && tasks.length > 0) {
                const lastTask = tasks[tasks.length - 1];
                console.log('Last task fetched:', lastTask);
                setLastToDoDate(lastTask.toDoDate);
                setLastCreatedAt(lastTask.createdAt);
            }
            setIsInitialLoaded(true);
        });
    }, [ownerId, assigneeId]);

    useEffect(() => {
        if (!isInitialLoaded) return;

        const handleUpdate = () => {
            const currentLength = done?.items?.length || 20;

            // Refresh current view - no date filtering, fetch the number of items currently displayed
            fetchTasks(
                httpClient,
                setDone,
                setAreAllTasksVisible,
                currentLength,
                ownerId,
                assigneeId,
                undefined, // No date filter when refreshing
                undefined  // No date filter when refreshing
            ).then((tasks) => {
                if (tasks && tasks.length > 0) {
                    const lastTask = tasks[tasks.length - 1];
                    setLastToDoDate(lastTask.toDoDate);
                    setLastCreatedAt(lastTask.createdAt);
                }
            });
        };

        realtime.onUpdate(handleUpdate);

        return () => realtime.clear();
    }, [isInitialLoaded, done?.items?.length, httpClient, setDone, ownerId, assigneeId, realtime]);

    const getMoreTasks = () => {
        if (!lastToDoDate || !lastCreatedAt) return;

        httpClient
            .get('tasks/completed', {
                params: {
                    All: true,
                    Take: 20,
                    CreatorId: ownerId,
                    AssigneeId: assigneeId,
                    ToDoDate: lastToDoDate,
                    CreatedAt: lastCreatedAt
                }
            })
            .then(res => {
                const newTasks = res.data.items || [];
                if (newTasks.length > 0) {
                    setDone({
                        items: [...(done?.items || []), ...newTasks]
                    });

                    const lastTask = newTasks[newTasks.length - 1];
                    setLastToDoDate(lastTask.toDoDate);
                    setLastCreatedAt(lastTask.createdAt);

                    if (newTasks.length < 20) {
                        setAreAllTasksVisible(true);
                    }
                } else {
                    setAreAllTasksVisible(true);
                }
            })
            .catch(err => console.error('Error loading more tasks:', err));
    };

    return (
        <>
            <Done
                done={done}
                getAllTasks={getMoreTasks}
                showButton={!areAllTasksVisible}
                setData={setDone}
                setAreAllTasksVisible={setAreAllTasksVisible}
            />
        </>
    );
};
